import { find, get, includes, isArray, isFunction, noop } from 'lodash'
import React, { useContext, useRef } from 'react'
import { Form } from 'react-bootstrap'
import { Field as FinalField } from 'react-final-form'

import FeedbackRender from './FeedbackRender'
import FieldContext from './FieldContext'
import Label from './Label'
import Readonly from './Readonly'
import { TField } from './types'

const Field = (props: TField): React.ReactElement => {
  const {
    name: fieldName = '',
    validate = noop,
    label = '',
    children = null,
    tooltip = '',
    required = false,
    isRequired = false,
    inline = false,
    inlineBlock = false,
    selectOnFocus = false,
    showCondition = null,
    initialValue = '',
    hideLabel = false,
    noLabel = false,
    noMargin = false,
    style = {},
    isClearable = false,
    isSpy,
    hideFeedback = false,
    hidden,
    ...rest
  } = props

  const { prefix } = useContext(FieldContext)

  let name: string = fieldName || (props.field as string)
  if (prefix) {
    name = `${prefix}.${fieldName}`
  }

  const inputRef = useRef<any>()

  if (
    isSpy &&
    (React.isValidElement(children) ||
      typeof children === 'string' ||
      typeof children === 'function')
  ) {
    return <Readonly {...props}>{props.children}</Readonly>
  }

  const inner = (
    <FinalField
      name={name}
      validate={validate}
      initialValue={initialValue}
      {...rest}
    >
      {(renderProps) => {
        const { input, meta } = renderProps

        const onFocus = (): void => {
          if (props.onFocus) {
            props.onFocus()
          }
          input.onFocus()
          if (selectOnFocus) {
            try {
              const node = inputRef.current
              node.select()
            } catch (error) {
              // console.log(error)
            }
          }
        }

        const onBlur = (): void => {
          if (props.onBlur) {
            props.onBlur()
          }
        }

        let feedbackType = ''
        let feedback

        const {
          dirtySinceLastSubmit,
          error,
          submitError,
          touched,
          validating,
        } = meta

        if (touched) {
          if (error || (submitError && !dirtySinceLastSubmit)) {
            feedbackType = 'invalid'
            feedback = error || submitError
          } else {
            feedbackType = 'valid'
          }
        }

        const onChange = (value): void => {
          input.onChange(value)
          if (isFunction(props.onChange)) {
            props.onChange(value)
          }
        }

        const onClear = (): void => {
          onChange(undefined)
        }

        const childProps = {
          name,
          onBlur,
          ...rest,
          ...input,
          onChange,
          onFocus,
          onClear: isClearable ? onClear : undefined,
          validate,
          ref: inputRef,
          isInvalid: feedbackType === 'invalid',
          isLoading: validating || props.isLoading,
          ['cy-name']: name,
          error,

          /** To be used by LockedInput component */
          parentFieldProps: props,
        }

        const formGroupStyle = {
          ...style,
        }

        if (inline) {
          formGroupStyle.display = 'inline'
        }

        if (inlineBlock) {
          formGroupStyle.display = 'inline-block'
        }

        const styleType = {
          styletype: feedbackType,
        }

        return (
          <Form.Group
            hidden={hidden}
            className={noMargin ? 'mb-0' : 'mb-1'}
            style={formGroupStyle}
          >
            {label && (
              <Label
                tooltip={tooltip}
                hideLabel={hideLabel || noLabel}
                label={label}
                required={required || isRequired}
              />
            )}
            {isFunction(children)
              ? children(childProps)
              : children
              ? React.cloneElement(children as React.ReactElement, childProps)
              : null}
            {!hideFeedback && (
              <Form.Control.Feedback
                style={{ display: 'inline' }}
                className={`${feedbackType}-feedback`}
                {...styleType}
              >
                <FeedbackRender feedback={feedback} />
              </Form.Control.Feedback>
            )}
          </Form.Group>
        )
      }}
    </FinalField>
  )

  if (!showCondition) {
    return inner
  }
  const { field: unprefixedField, value } = showCondition
  let field = unprefixedField
  if (prefix) {
    field = `${prefix}.${unprefixedField}`
  }

  if (value === 'truthifies') {
    return (
      <FinalField name={field} subscription={{ value: true }}>
        {({ input: { value: v } }) => {
          if (v || v.toString().trim() === '') return inner
          return null
        }}
      </FinalField>
    )
  }
  if (value === 'truthifiesWithOutEmpty') {
    return (
      <FinalField name={field} subscription={{ value: true }}>
        {({ input: { value: v } }) => {
          if (v || v.toString().trim() !== '') return inner
          return null
        }}
      </FinalField>
    )
  }
  return (
    <FinalField name={field} subscription={{ value: true }}>
      {({ input: { value: v } }) => {
        if (isArray(v)) {
          return includes(get(v, 'value', v), value) ||
            find(get(v, 'value', v), (option) => option.value === value)
            ? inner
            : null
        }
        return get(v, 'value', v) === value && inner
      }}
    </FinalField>
  )
}

export default Field

export { default as OnChange } from './OnChange'
export { default as Readonly } from './Readonly'
export { default as FieldContext } from './FieldContext'
export { default as FieldContextProvider } from './FieldContextProvider'
export { default as FieldPrefix } from './FieldPrefix'
export { default as useFieldContext } from './useFieldContext'
export { default as Fields } from '../Fields'
export { default as useField } from './useField'
export { default as Label } from './Label'
export { default as AsyncValidationField } from './AsyncValidationField'
export { default as CustomValidator } from './CustomValidator'
export { default as Feedback } from './Feedback'
export { default as FeedbackBadge } from './FeedbackBadge'

export {
  composeValidators,
  required,
  mustBeFactor,
  mustBeSamePassword,
  mustBeEmail,
  mustbeAfterStartOrUntilCancellation,
  street_mustNotBeANumber,
  street_number_mustBeANumber,
  mustBeValidCallable,
  minLength,
  lengthRange,
  requiredWithPresentOption,
  exactLength,
  nowOrPast,
  mustBeNumber,
  mustNotBeDezimalNumber,
  maxValue,
  minValue,
  mustbeIBAN,
  valuesMustSolveToZero,
  mustBePositive,
  mustBePositiveOrZero,
  mustBePositiveOrEmpty,
  mustBeValidCountryZip,
  validateLat,
  validateLon,
  mustBePhoneNumber,
  mustBeAllUpperCase,
  mustBePositiveRealNumber,
  mustBeNumberUpdate,
} from './Validation'
